
/// React Hook that listens to window.location path changes and translates these to board and session route ids

import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { TEMPLATES } from "../models/template";

export type PageKind = "home" | "privacy" | "pricing" | "templates" | "templateDetail" | "terms" | "blog" | "blogPost" | "trySession";

const PAGE_ORDER: Record<PageKind, number> = {
  home: 0,
  templates: 1,
  templateDetail: 1,
  blog: 1,
  blogPost: 1,
  pricing: 2,
  privacy: 3,
  terms: 4,
  // trySession is a workspace destination — deeper than any supporting page.
  trySession: 5,
};

// "Flat" = the mostly-blue supporting pages. Transitions to/from any of these
// use a vertical slide instead of the default horizontal one, so they feel
// like a lift-off rather than a peer-page swap.
const FLAT_PAGES: ReadonlySet<PageKind> = new Set(["pricing", "terms", "privacy", "templateDetail", "blog", "blogPost"]);

const KNOWN_TEMPLATE_IDS = new Set(TEMPLATES.map((t) => t.id));

// Blog post slugs pattern: YYYY-MM-DD-kebab-title. Kept as a regex rather than
// an enum so adding a new post only requires the registry + prerender list
// updates, not a change here.
const BLOG_SLUG_PATTERN = /^\d{4}-\d{2}-\d{2}-[a-z0-9-]+$/;

function getTransitionDirection(
  oldRoute: RouteState,
  newRoute: RouteState,
  isBack: boolean,
): string {
  // Entering a session: forward
  if (!oldRoute.sessionUid && newRoute.sessionUid) return "forward";
  // Leaving a session: back
  if (oldRoute.sessionUid && !newRoute.sessionUid) return "back";
  // Horizontal navigation between pages
  const oldOrder = PAGE_ORDER[oldRoute.page] ?? 0;
  const newOrder = PAGE_ORDER[newRoute.page] ?? 0;
  return isBack || newOrder < oldOrder ? "back" : "forward";
}

function getTransitionAxis(oldRoute: RouteState, newRoute: RouteState): "horizontal" | "vertical" {
  return FLAT_PAGES.has(oldRoute.page) || FLAT_PAGES.has(newRoute.page) ? "vertical" : "horizontal";
}

export interface RouteState {
  path: string;
  page: PageKind;
  boardUid: string | undefined;
  sessionUid: string | undefined;
  templateId: string | undefined;
  slug: string | undefined;
  from: string | undefined;
  encodedState: string | undefined;
}

export function parsePath(pathname: string, search: string = ""): RouteState {
  const segments = pathname.replace(/^\//, "").split("/").filter(Boolean);
  const fullPath = pathname + (search.startsWith("?") ? search : search ? `?${search}` : "");
  const base = { path: fullPath, boardUid: undefined, sessionUid: undefined, templateId: undefined, slug: undefined, from: undefined, encodedState: undefined };

  // /board/{boardUid}/session/{sessionUid}
  if (segments[0] === "board" && segments[2] === "session" && segments.length === 4) {
    return { ...base, page: "home", boardUid: segments[1], sessionUid: segments[3] };
  }

  // /board/{boardUid}
  if (segments[0] === "board" && segments.length === 2) {
    return { ...base, page: "home", boardUid: segments[1] };
  }

  // /privacy
  if (segments[0] === "privacy" && segments.length === 1) {
    return { ...base, page: "privacy" };
  }

  // /terms
  if (segments[0] === "terms" && segments.length === 1) {
    return { ...base, page: "terms" };
  }

  // /pricing
  if (segments[0] === "pricing" && segments.length === 1) {
    return { ...base, page: "pricing" };
  }

  // /templates/{templateId} — must precede the /templates match. Unknown IDs fall through to home.
  // `from` is deliberately NOT parsed from the URL — origin is tracked via
  // in-memory route state (see templateDetailRoute in RouteProvider).
  if (segments[0] === "templates" && segments.length === 2 && KNOWN_TEMPLATE_IDS.has(segments[1])) {
    return { ...base, page: "templateDetail", templateId: segments[1] };
  }

  // /templates
  if (segments[0] === "templates" && segments.length === 1) {
    return { ...base, page: "templates" };
  }

  // /blog/{slug} — must precede the /blog match. Unknown-shape slugs fall through
  // to /blog rather than home, so a stale link lands the user on the list.
  if (segments[0] === "blog" && segments.length === 2 && BLOG_SLUG_PATTERN.test(segments[1])) {
    return { ...base, page: "blogPost", slug: segments[1] };
  }

  // /blog
  if (segments[0] === "blog" && segments.length === 1) {
    return { ...base, page: "blog" };
  }

  // /try/{templateId}[/{encodedState}] — URL-encoded ephemeral session. No auth,
  // no persistence, state lives in the URL. Unknown template IDs fall through.
  if (segments[0] === "try" && (segments.length === 2 || segments.length === 3) && KNOWN_TEMPLATE_IDS.has(segments[1])) {
    return { ...base, page: "trySession", templateId: segments[1], encodedState: segments[2] };
  }

  // home
  return { ...base, page: "home" };
}

/// setState function allows new board and session ids to be pushed to the window.location path
export const useRouteState = (): [RouteState, (newRoute: RouteState) => void] => {
  const [route, setRoute] = useState<RouteState>(() =>
    parsePath(window.location.pathname, window.location.search),
  );
  const routeRef = useRef(route);
  routeRef.current = route;

  const transitionTo = useCallback((newRoute: RouteState, isBack = false) => {
    const prefersReducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (document.startViewTransition && !prefersReducedMotion) {
      const direction = getTransitionDirection(routeRef.current, newRoute, isBack);
      const axis = getTransitionAxis(routeRef.current, newRoute);
      document.documentElement.dataset.vtDirection = direction;
      document.documentElement.dataset.vtAxis = axis;

      const transition = document.startViewTransition(() => {
        flushSync(() => setRoute(newRoute));
      });
      transition.finished.then(() => {
        delete document.documentElement.dataset.vtDirection;
        delete document.documentElement.dataset.vtAxis;
      });
    } else {
      setRoute(newRoute);
    }
  }, []);

  const readRouteFromPath = useCallback(() => {
    const pathname = window.location.pathname;
    const search = window.location.search;
    const currentRoute = routeRef.current;
    const nextPath = pathname + search;

    if (nextPath === currentRoute.path) {
      return;
    }

    transitionTo(parsePath(pathname, search), true);
  }, [transitionTo]);

  const replaceRoute: (newRoute: RouteState) => void = useCallback((newRoute: RouteState) => {
    if (newRoute.path !== routeRef.current.path) {
      window.history.pushState(null, "", newRoute.path);
      transitionTo(newRoute);
    }
  }, [transitionTo]);

  useEffect(() => {
    window.addEventListener("popstate", readRouteFromPath);
    readRouteFromPath();

    return () => {
      window.removeEventListener("popstate", readRouteFromPath);
    };
  }, [readRouteFromPath]);

  return [route, replaceRoute];
};
